import { Reveal } from "../components/Reveal";
import { Target, Eye, Sparkles, ShieldCheck, Handshake, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

const VALUES = [
  { icon: ShieldCheck, title: "Integrity", body: "Uncompromising ethics in every filing, opinion and agreement we deliver." },
  { icon: Award, title: "Excellence", body: "Precision, depth and diligence — the hallmarks of every engagement." },
  { icon: Handshake, title: "Partnership", body: "Your business goals become ours. We work alongside — never around — you." },
  { icon: Sparkles, title: "Clarity", body: "We translate complex regulation into confident, actionable business decisions." },
];

const MILESTONES = [
  { year: "Foundation", title: "Built for modern India", body: "Founded to bring boutique legal rigour to India's fast-scaling businesses." },
  { year: "Multi-vertical", title: "15+ compliance streams", body: "From FSSAI to M&A — deep specialists across every regulatory domain." },
  { year: "CA-collaborative", title: "Integrated advisory", body: "Financial, tax & GST due diligence delivered with chartered accountants." },
  { year: "Today", title: "Trusted by 500+ businesses", body: "SMEs, startups and mid-market corporates across India rely on KaveriBiz." },
];

export default function About() {
  return (
    <div className="pt-36 pb-8" data-testid="about-page">
      <Seo path="/about" title={PAGE_SEO["/about"].title} description={PAGE_SEO["/about"].description} />
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="kb-label mb-6">About KaveriBiz</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] max-w-4xl">
            The compliance partner behind <span className="italic text-[#d4a840]">bold</span> Indian businesses.
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            KaveriBiz Corporate Services LLP is a full-spectrum corporate compliance, licensing
            and legal services firm — trusted by founders, promoters and CFOs to keep their
            businesses statutorily sound and strategically agile.
          </p>
        </Reveal>
      </section>

      {/* Mission & Vision */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 grid md:grid-cols-2 gap-6">
        <Reveal>
          <div className="kb-glass rounded-2xl p-10 h-full" data-testid="about-mission">
            <div className="w-12 h-12 rounded-xl bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center mb-6">
              <Target size={22} className="text-[#d4a840]" />
            </div>
            <div className="kb-label mb-3">Mission</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
              Make world-class compliance <span className="italic text-[#d4a840]">effortless</span> for every Indian business.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              We remove friction, ambiguity and delay from the regulatory journey — so founders
              can focus on building extraordinary companies.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="kb-glass rounded-2xl p-10 h-full" data-testid="about-vision">
            <div className="w-12 h-12 rounded-xl bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center mb-6">
              <Eye size={22} className="text-[#d4a840]" />
            </div>
            <div className="kb-label mb-3">Vision</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
              India's most <span className="italic text-[#d4a840]">trusted</span> corporate services firm — one client at a time.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              To be the single accountable partner for every regulatory, licensing and legal
              milestone in a company's lifecycle — from Day 1 to IPO and beyond.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Timeline */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
        <Reveal>
          <div className="kb-label mb-4">Our Story</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight max-w-2xl">
            Built on <span className="italic text-[#d4a840]">rigor.</span> Delivered with care.
          </h2>
        </Reveal>

        <div className="mt-14 relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#d4a840]/50 to-transparent" />
          <div className="space-y-14">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.08}>
                <div className={`grid md:grid-cols-2 gap-8 items-center relative ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
                  <div className={`kb-glass rounded-2xl p-8 md:[direction:ltr]`} data-testid={`timeline-${i}`}>
                    <div className="kb-label mb-3">{m.year}</div>
                    <h3 className="font-display text-2xl font-semibold text-white">{m.title}</h3>
                    <p className="text-white/65 mt-3 leading-relaxed">{m.body}</p>
                  </div>
                  <div className="relative hidden md:flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full bg-[#d4a840] shadow-[0_0_20px_rgba(212,168,64,0.7)]" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
        <Reveal>
          <div className="kb-label mb-4">Our Values</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight max-w-2xl">
            Four principles. <span className="italic text-[#d4a840]">Zero compromise.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="kb-glass rounded-2xl p-7 h-full" data-testid={`value-${v.title.toLowerCase()}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center mb-5">
                    <Icon size={20} className="text-[#d4a840]" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-white">{v.title}</h3>
                  <p className="text-white/65 text-sm mt-2 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 mt-32 text-center">
        <Reveal>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight">
            Ready to <span className="italic text-[#d4a840]">partner up?</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-xl mx-auto">
            Meet the team behind India's fastest-scaling businesses' compliance.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="kb-btn-primary" data-testid="about-cta">Speak with our team</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
